/**
 * Service for processing OCR text with Google Gemini AI
 * Extracts structured business card information from raw OCR text
 */

const GEMINI_API_KEY = import.meta.env.VITE_GEMINI_API_KEY;
const GEMINI_API_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-3-pro-preview:generateContent';
const GEMINI_IMAGE_API_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash-exp-image-generation:generateContent';

/**
 * Analyzes OCR text with Gemini to extract business card information
 * @param {string} ocrText - Raw text extracted from OCR
 * @param {string} language - Language code ('fr' or 'en')
 * @returns {Promise<Object>} Extracted and structured data
 */
export const analyzeBusinessCardWithGemini = async (ocrText, language = 'fr') => {
    if (!GEMINI_API_KEY) {
        console.error('Gemini API key not found in environment variables');
        throw new Error('Gemini API key is not configured');
    }

    const prompt = language === 'fr'
        ? `Analyse ce texte extrait d'une carte de visite par OCR et extrait les informations suivantes au format JSON strict.

IMPORTANT: Réponds UNIQUEMENT avec un objet JSON valide, sans aucun texte avant ou après.

Texte OCR:
"""
${ocrText}
"""

Extrait ces champs (laisse vide "" si non trouvé):
- firstName: Prénom
- lastName: Nom de famille
- email: Adresse email
- phone: Numéro de téléphone (format international si possible)
- company: Nom de l'entreprise
- position: Fonction/poste
- address: Adresse complète
- website: Site web (avec https:// si trouvé)

Réponds UNIQUEMENT avec ce format JSON:
{
  "firstName": "",
  "lastName": "",
  "email": "",
  "phone": "",
  "company": "",
  "position": "",
  "address": "",
  "website": ""
}`
        : `Analyze this text extracted from a business card via OCR and extract the following information in strict JSON format.

IMPORTANT: Respond ONLY with a valid JSON object, no text before or after.

OCR Text:
"""
${ocrText}
"""

Extract these fields (leave empty "" if not found):
- firstName: First name
- lastName: Last name
- email: Email address
- phone: Phone number (international format if possible)
- company: Company name
- position: Job title/position
- address: Full address
- website: Website (with https:// if found)

Respond ONLY with this JSON format:
{
  "firstName": "",
  "lastName": "",
  "email": "",
  "phone": "",
  "company": "",
  "position": "",
  "address": "",
  "website": ""
}`;

    try {
        console.log('Sending request to Gemini API...');

        const response = await fetch(GEMINI_API_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-goog-api-key': GEMINI_API_KEY,
            },
            body: JSON.stringify({
                contents: [{
                    parts: [{
                        text: prompt
                    }]
                }]
            })
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error('Gemini API error:', errorText);
            throw new Error(`Gemini API error: ${response.status}`);
        }

        const data = await response.json();
        //console.log('Gemini API full response:', JSON.stringify(data, null, 2));

        // Check different possible response structures
        let generatedText = '';

        if (data.candidates && data.candidates[0]) {
            if (data.candidates[0].content && data.candidates[0].content.parts) {
                generatedText = data.candidates[0].content.parts[0].text;
            } else if (data.candidates[0].text) {
                generatedText = data.candidates[0].text;
            } else if (data.candidates[0].output) {
                generatedText = data.candidates[0].output;
            }
        } else if (data.text) {
            generatedText = data.text;
        } else if (data.output) {
            generatedText = data.output;
        }

        if (!generatedText) {
            console.error('Could not find text in response structure:', data);
            throw new Error('Invalid response format from Gemini API - no text found');
        }

        console.log('Generated text:', generatedText);

        // Extract JSON from the response (handle cases where there might be extra text)
        const jsonMatch = generatedText.match(/\{[\s\S]*\}/);
        if (!jsonMatch) {
            throw new Error('No JSON found in Gemini response');
        }

        const extractedData = JSON.parse(jsonMatch[0]);
        console.log('Extracted data from Gemini:', extractedData);

        // Filter out empty values
        const filteredData = {};
        for (const [key, value] of Object.entries(extractedData)) {
            if (value && value.trim() !== '') {
                filteredData[key] = value.trim();
            }
        }

        return filteredData;

    } catch (error) {
        console.error('Error analyzing with Gemini:', error);
        throw error;
    }
};

/**
 * Check if Gemini API is available and configured
 * @returns {boolean} True if Gemini is available
 */
export const isGeminiAvailable = () => {
    return !!GEMINI_API_KEY;
};

/**
 * Enhances a product image using Gemini AI
 * Can improve quality, remove background, or place product in a new context
 * @param {string} imageUrl - URL of the image to enhance
 * @param {Object} options - Enhancement options
 * @param {string} options.mode - Enhancement mode: 'enhance', 'removeBackground', 'changeContext'
 * @param {string} options.customPrompt - Custom prompt for specific transformations
 * @param {string} options.context - New context/background description (for changeContext mode)
 * @param {string} options.language - Language code for prompts
 * @returns {Promise<Object>} { success, enhancedBase64, mimeType, error }
 */
export const enhanceImageWithAI = async (imageUrl, options = {}) => {
    const {
        mode = 'enhance',
        customPrompt = '',
        context = '',
        language = 'fr'
    } = options;

    if (!GEMINI_API_KEY) {
        console.error('Gemini API key not found');
        return { success: false, error: 'Gemini API key is not configured' };
    }

    try {
        // Fetch the image and convert to base64
        console.log('Fetching image from:', imageUrl);
        const imageResponse = await fetch(imageUrl);
        if (!imageResponse.ok) {
            throw new Error(`Failed to fetch image: ${imageResponse.status}`);
        }

        const imageBlob = await imageResponse.blob();
        const mimeType = imageBlob.type || 'image/jpeg';

        // Convert blob to base64
        const base64Image = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64 = reader.result.split(',')[1];
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(imageBlob);
        });

        // Build the prompt based on mode
        let promptText = '';

        if (customPrompt) {
            // Use custom prompt directly
            promptText = customPrompt;
        } else {
            const prompts = {
                enhance: {
                    fr: `Améliore cette photo de produit pour une annonce de vente :
- Améliore la luminosité et le contraste
- Rends les couleurs plus vives et attrayantes
- Nettoie l'arrière-plan si nécessaire
- Améliore la netteté du produit
- Garde le produit exactement identique
Retourne uniquement l'image améliorée.`,
                    en: `Enhance this product photo for a sales listing:
- Improve brightness and contrast
- Make colors more vivid and attractive
- Clean up the background if needed
- Enhance product sharpness
- Keep the product exactly the same
Return only the enhanced image.`
                },
                removeBackground: {
                    fr: `Détoure précisément le produit principal de cette image :
- Supprime complètement l'arrière-plan
- Remplace-le par un fond blanc pur (#FFFFFF)
- Garde le produit parfaitement intact avec tous ses détails
- Assure des bords nets et propres autour du produit
Retourne uniquement l'image avec le fond blanc.`,
                    en: `Precisely cut out the main product from this image:
- Completely remove the background
- Replace it with a pure white background (#FFFFFF)
- Keep the product perfectly intact with all its details
- Ensure clean, sharp edges around the product
Return only the image with white background.`
                },
                changeContext: {
                    fr: `Détoure le produit principal de cette image et place-le dans un nouveau contexte :
- Extrais précisément le produit de l'image
- Place-le dans ce nouveau décor : ${context || 'un studio photo professionnel avec un éclairage doux'}
- Le produit doit être mis en valeur et bien visible
- Assure une intégration naturelle avec des ombres réalistes
- Garde le produit exactement identique
Retourne uniquement l'image finale.`,
                    en: `Cut out the main product from this image and place it in a new context:
- Precisely extract the product from the image
- Place it in this new setting: ${context || 'a professional photo studio with soft lighting'}
- The product should be highlighted and clearly visible
- Ensure natural integration with realistic shadows
- Keep the product exactly the same
Return only the final image.`
                }
            };

            promptText = prompts[mode]?.[language] || prompts.enhance.fr;
        }

        console.log('Sending image to Gemini for enhancement, mode:', mode);

        const response = await fetch(GEMINI_IMAGE_API_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-goog-api-key': GEMINI_API_KEY,
            },
            body: JSON.stringify({
                contents: [{
                    parts: [
                        { text: promptText },
                        {
                            inline_data: {
                                mime_type: mimeType,
                                data: base64Image
                            }
                        }
                    ]
                }],
                generationConfig: {
                    responseModalities: ['image', 'text']
                }
            })
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error('Gemini Image API error:', errorText);
            throw new Error(`Gemini API error: ${response.status}`);
        }

        const data = await response.json();
        console.log('Gemini response received');

        // Extract the enhanced image from response
        if (data.candidates && data.candidates[0]?.content?.parts) {
            for (const part of data.candidates[0].content.parts) {
                if (part.inlineData) {
                    console.log('Enhanced image received successfully');
                    return {
                        success: true,
                        enhancedBase64: part.inlineData.data,
                        mimeType: part.inlineData.mimeType || 'image/png'
                    };
                }
            }
        }

        throw new Error('No image found in Gemini response');

    } catch (error) {
        console.error('Error enhancing image with AI:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Generates a place description using Gemini AI based on Google Place data
 * Gemini will also browse the website to get more context about the business
 * @param {Object} placeData - Data from Google Places API
 * @param {string} language - Language code ('fr', 'en', 'nl', etc.)
 * @returns {Promise<string>} Generated description
 */
export const generatePlaceDescription = async (placeData, language = 'fr') => {
    if (!GEMINI_API_KEY) {
        console.error('Gemini API key not found in environment variables');
        throw new Error('Gemini API key is not configured');
    }

    const langInstructions = {
        fr: 'Écris en français',
        en: 'Write in English',
        nl: 'Schrijf in het Nederlands',
        de: 'Schreibe auf Deutsch',
        es: 'Escribe en español',
        it: 'Scrivi in italiano',
        pt: 'Escreva em português',
        el: 'Γράψε στα ελληνικά'
    };

    const langInstruction = langInstructions[language] || langInstructions.fr;

    // Build context from available place data
    const contextParts = [];
    if (placeData.name) contextParts.push(`Nom de l'entreprise: ${placeData.name}`);
    if (placeData.category) contextParts.push(`Catégorie: ${placeData.category}`);
    if (placeData.address) contextParts.push(`Adresse: ${placeData.address}`);
    if (placeData.city) contextParts.push(`Ville: ${placeData.city}`);
    if (placeData.country) contextParts.push(`Pays: ${placeData.country}`);
    if (placeData.website) contextParts.push(`Site web: ${placeData.website}`);
    if (placeData.googleTypes && placeData.googleTypes.length > 0) {
        contextParts.push(`Types d'activité Google: ${placeData.googleTypes.join(', ')}`);
    }
    if (placeData.editorialSummary) {
        contextParts.push(`Description Google: ${placeData.editorialSummary}`);
    }

    const context = contextParts.join('\n');

    // Build the prompt with instructions to browse the website
    const websiteInstruction = placeData.website
        ? `\n\nIMPORTANT: Consulte le site web ${placeData.website} pour comprendre ce que l'entreprise fait, ses produits/services, son histoire et sa mission. Utilise ces informations pour enrichir la description.`
        : '';

    const prompt = `${langInstruction}.

Tu es un rédacteur professionnel spécialisé dans les descriptions d'entreprises.

Voici les informations disponibles sur cet établissement:
${context}
${websiteInstruction}

Ta mission: Rédige une description professionnelle et engageante qui explique:
1. Ce que l'entreprise fait concrètement (produits, services, activité principale)
2. Ce qui la rend unique ou son expertise particulière
3. Pourquoi elle est établie à cet endroit (si pertinent)

Règles strictes:
- Maximum 4-5 phrases (environ 60-100 mots)
- Ton professionnel mais chaleureux
- Concentre-toi sur l'ACTIVITÉ et les PRODUITS/SERVICES de l'entreprise
- Ne mentionne PAS les notes, avis, horaires ou coordonnées
- Commence directement par la description (pas de "Voici..." ou "Cette entreprise...")
- Si tu consultes le site web, utilise les informations pertinentes trouvées

Réponds UNIQUEMENT avec le texte de description, sans guillemets ni formatage.`;

    try {
        console.log('Generating place description with Gemini (with web browsing)...');

        // Use Gemini with tools for URL context (grounding)
        const requestBody = {
            contents: [{
                parts: [{
                    text: prompt
                }]
            }]
        };

        // Add Google Search grounding tool if website is provided
        if (placeData.website) {
            requestBody.tools = [{
                google_search: {}
            }];
        }

        const response = await fetch(GEMINI_API_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-goog-api-key': GEMINI_API_KEY,
            },
            body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error('Gemini API error:', errorText);
            throw new Error(`Gemini API error: ${response.status}`);
        }

        const data = await response.json();

        // Extract text from response
        let generatedText = '';
        if (data.candidates && data.candidates[0]) {
            if (data.candidates[0].content && data.candidates[0].content.parts) {
                generatedText = data.candidates[0].content.parts[0].text;
            } else if (data.candidates[0].text) {
                generatedText = data.candidates[0].text;
            }
        }

        if (!generatedText) {
            throw new Error('No text found in Gemini response');
        }

        // Clean up the response
        generatedText = generatedText.trim();
        // Remove quotes if present
        if ((generatedText.startsWith('"') && generatedText.endsWith('"')) ||
            (generatedText.startsWith('«') && generatedText.endsWith('»'))) {
            generatedText = generatedText.slice(1, -1).trim();
        }

        console.log('Generated description:', generatedText);
        return generatedText;

    } catch (error) {
        console.error('Error generating description with Gemini:', error);
        throw error;
    }
};

/**
 * Analyzes a website URL with Gemini AI
 * Generates title, description, keywords, category in ONE language + contact info
 * @param {string} url - Website URL to analyze
 * @param {string} language - Language code (fr, en, nl, de, es, it, pt, el)
 * @returns {Promise<Object>} Extracted website data for the specified language
 */
export const analyzeWebsite = async (url, language = 'fr') => {
    if (!GEMINI_API_KEY) {
        throw new Error('Gemini API key is not configured');
    }

    const langNames = {
        fr: 'français',
        en: 'English',
        nl: 'Nederlands',
        de: 'Deutsch',
        es: 'español',
        it: 'italiano',
        pt: 'português',
        el: 'ελληνικά'
    };

    const langName = langNames[language] || langNames.fr;

    const prompt = `Tu es un expert en analyse de sites web.

URL à analyser: ${url}

IMPORTANT - RÈGLES STRICTES:
1. Analyse UNIQUEMENT le contenu visible sur ce site web
2. Ne fais PAS de suppositions sur l'entreprise
3. Base-toi UNIQUEMENT sur les informations présentes sur le site
4. Si une information n'est pas sur le site, laisse le champ VIDE
5. Génère le contenu UNIQUEMENT en ${langName}

Ta mission:
1. Extrais les balises meta (title, description, keywords) de la page
2. Analyse le contenu textuel visible de la page
3. Génère un titre accrocheur (max 60 caractères) en ${langName}
4. Génère une description engageante (150-300 mots) en ${langName}
5. Génère des mots-clés pertinents (5-10) en ${langName}
6. Génère la catégorie en ${langName}

Réponds UNIQUEMENT avec un objet JSON valide contenant:
{
    "Title_${language}": "Titre en ${langName}",
    "Description_${language}": "Description en ${langName}...",
    "Keywords_${language}": "mots-clés, séparés, par, virgules",
    "Category_${language}": "Catégorie",
    "ContactEmail": "email trouvé ou vide",
    "ContactPhone": "téléphone trouvé ou vide",
    "Address": "adresse trouvée ou vide",
    "SocialLinks": {"facebook": "", "linkedin": "", "instagram": "", "twitter": "", "youtube": ""}
}

RAPPEL: Ne devine PAS les informations de contact. Laisse vide si non trouvé sur le site.`;

    try {
        console.log(`Analyzing website with Gemini (${language}):`, url);

        const requestBody = {
            contents: [{
                parts: [{
                    text: prompt
                }]
            }],
            tools: [{
                google_search: {}
            }]
        };

        const response = await fetch(GEMINI_API_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-goog-api-key': GEMINI_API_KEY,
            },
            body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error('Gemini API error:', errorText);
            throw new Error(`Gemini API error: ${response.status}`);
        }

        const data = await response.json();

        // Extract text from response
        let generatedText = '';
        if (data.candidates && data.candidates[0]) {
            if (data.candidates[0].content && data.candidates[0].content.parts) {
                for (const part of data.candidates[0].content.parts) {
                    if (part.text) {
                        generatedText += part.text;
                    }
                }
            } else if (data.candidates[0].text) {
                generatedText = data.candidates[0].text;
            }
        }

        if (!generatedText) {
            throw new Error('No text found in Gemini response');
        }

        // Extract JSON from the response
        const jsonMatch = generatedText.match(/\{[\s\S]*\}/);
        if (!jsonMatch) {
            throw new Error('No JSON found in Gemini response');
        }

        const extractedData = JSON.parse(jsonMatch[0]);

        // Ensure SocialLinks is a string for the API
        if (extractedData.SocialLinks && typeof extractedData.SocialLinks === 'object') {
            extractedData.SocialLinks = JSON.stringify(extractedData.SocialLinks);
        }

        console.log(`Website analysis completed (${language})`);
        return extractedData;

    } catch (error) {
        console.error('Error analyzing website with Gemini:', error);
        throw error;
    }
};

/**
 * Translates website content from source language to all other languages
 * @param {Object} sourceContent - Content in source language { title, description, keywords, category }
 * @param {string} sourceLang - Source language code
 * @returns {Promise<Object>} Translations for all 7 other languages
 */
/**
 * Enhances text content using Gemini AI
 * Improves clarity, grammar, and professional tone
 * @param {string} text - Text to enhance
 * @param {string} language - Language code ('fr' or 'en')
 * @returns {Promise<string>} Enhanced text
 */
export const enhanceTextWithAI = async (text, language = 'fr') => {
    if (!GEMINI_API_KEY) {
        console.error('Gemini API key not found');
        return text;
    }

    if (!text || text.trim().length < 10) {
        return text;
    }

    const prompt = language === 'fr'
        ? `Améliore ce texte de notes/description pour un contact professionnel :
- Corrige l'orthographe et la grammaire
- Améliore la clarté et la lisibilité
- Garde un ton professionnel mais naturel
- Conserve le sens original
- Ne change pas radicalement le contenu

Texte original:
"""
${text}
"""

Réponds UNIQUEMENT avec le texte amélioré, sans guillemets ni explications.`
        : `Enhance this notes/description text for a professional contact:
- Fix spelling and grammar
- Improve clarity and readability
- Keep a professional but natural tone
- Preserve the original meaning
- Don't drastically change the content

Original text:
"""
${text}
"""

Respond ONLY with the enhanced text, no quotes or explanations.`;

    try {
        const response = await fetch(GEMINI_API_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-goog-api-key': GEMINI_API_KEY,
            },
            body: JSON.stringify({
                contents: [{
                    parts: [{
                        text: prompt
                    }]
                }]
            })
        });

        if (!response.ok) {
            console.error('Gemini API error:', response.status);
            return text;
        }

        const data = await response.json();

        let generatedText = '';
        if (data.candidates && data.candidates[0]) {
            if (data.candidates[0].content && data.candidates[0].content.parts) {
                generatedText = data.candidates[0].content.parts[0].text;
            } else if (data.candidates[0].text) {
                generatedText = data.candidates[0].text;
            }
        }

        if (!generatedText) {
            return text;
        }

        // Clean up response
        generatedText = generatedText.trim();
        if ((generatedText.startsWith('"') && generatedText.endsWith('"')) ||
            (generatedText.startsWith('«') && generatedText.endsWith('»'))) {
            generatedText = generatedText.slice(1, -1).trim();
        }

        return generatedText;

    } catch (error) {
        console.error('Error enhancing text with AI:', error);
        return text;
    }
};

export const translateWebsiteContent = async (sourceContent, sourceLang = 'fr') => {
    if (!GEMINI_API_KEY) {
        throw new Error('Gemini API key is not configured');
    }

    const allLangs = ['fr', 'en', 'nl', 'de', 'es', 'it', 'pt', 'el'];
    const targetLangs = allLangs.filter(l => l !== sourceLang);

    const langNames = {
        fr: 'français',
        en: 'English',
        nl: 'Nederlands',
        de: 'Deutsch',
        es: 'español',
        it: 'italiano',
        pt: 'português',
        el: 'ελληνικά'
    };

    const prompt = `Tu es un traducteur professionnel expert.

Contenu source (${langNames[sourceLang]}):
- Titre: ${sourceContent.title || ''}
- Description: ${sourceContent.description || ''}
- Mots-clés: ${sourceContent.keywords || ''}
- Catégorie: ${sourceContent.category || ''}

Ta mission: Traduis ce contenu dans les 7 langues suivantes de manière précise et naturelle.
Adapte le style et les expressions à chaque langue cible.

Réponds UNIQUEMENT avec un objet JSON valide:
{
${targetLangs.map(lang => `    "Title_${lang}": "traduction du titre en ${langNames[lang]}",
    "Description_${lang}": "traduction de la description en ${langNames[lang]}",
    "Keywords_${lang}": "traduction des mots-clés en ${langNames[lang]}",
    "Category_${lang}": "traduction de la catégorie en ${langNames[lang]}"`).join(',\n')}
}`;

    try {
        console.log(`Translating website content from ${sourceLang} to 7 languages...`);

        const response = await fetch(GEMINI_API_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-goog-api-key': GEMINI_API_KEY,
            },
            body: JSON.stringify({
                contents: [{
                    parts: [{
                        text: prompt
                    }]
                }]
            })
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error('Gemini API error:', errorText);
            throw new Error(`Gemini API error: ${response.status}`);
        }

        const data = await response.json();

        // Extract text from response
        let generatedText = '';
        if (data.candidates && data.candidates[0]) {
            if (data.candidates[0].content && data.candidates[0].content.parts) {
                for (const part of data.candidates[0].content.parts) {
                    if (part.text) {
                        generatedText += part.text;
                    }
                }
            } else if (data.candidates[0].text) {
                generatedText = data.candidates[0].text;
            }
        }

        if (!generatedText) {
            throw new Error('No text found in Gemini response');
        }

        // Extract JSON from the response
        const jsonMatch = generatedText.match(/\{[\s\S]*\}/);
        if (!jsonMatch) {
            throw new Error('No JSON found in Gemini response');
        }

        const translations = JSON.parse(jsonMatch[0]);
        console.log('Translation completed for 7 languages');
        return translations;

    } catch (error) {
        console.error('Error translating website content:', error);
        throw error;
    }
};
